import {
  Outlet,
  createRootRoute,
  redirect,
  HeadContent,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useAuthStore } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { UnauthorizedError } from "@/lib/api";

/**
 * 404 Not Found page component.
 * Displays a friendly error message and provides navigation options.
 */
function NotFound() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/80 flex flex-col">
      {/* Header */}
      <header className="container flex h-14 items-center">
        <a
          href="/"
          className="flex items-center gap-2 font-semibold hover:opacity-90 transition-opacity"
        >
          <MessageSquare className="h-6 w-6" />
          <span>aiEmailAgent</span>
        </a>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center">
        <div className="container max-w-[64rem] flex flex-col items-center text-center gap-8 py-12">
          <div className="space-y-4">
            <h1 className="text-4xl sm:text-6xl font-bold tracking-tighter">
              404 - Page Not Found
            </h1>
            <p className="text-xl text-muted-foreground max-w-[42rem] mx-auto">
              Oops! The page you're looking for seems to have wandered off.
              Let's get you back on track.
            </p>
          </div>

          {/* Glass Card */}
          <div className="glass rounded-xl p-8 shadow-lg border w-full max-w-md">
            <div className="space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">Lost?</h2>
                <p className="text-muted-foreground">
                  Don't worry, we've all been there. Here are some helpful
                  links:
                </p>
              </div>

              <div className="space-y-4">
                <Button asChild className="w-full" size="lg">
                  <a href="/">Return Home</a>
                </Button>
                <Button asChild variant="outline" className="w-full" size="lg">
                  <a href="/dashboard">Go to Dashboard</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="container py-6 flex items-center">
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} aiEmailAgent. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

/**
 * Root route configuration for the application.
 * Handles global concerns like authentication checks before loading routes.
 */
export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
  /**
   * Before loading any route, perform checks:
   * 1. Validate token if accessing auth or protected routes using refreshUser.
   * 2. Explicitly logout within beforeLoad if validation fails.
   * 3. Redirect authenticated users away from auth paths.
   * 4. Redirect unauthenticated users away from protected paths.
   */
  beforeLoad: async ({ location }) => {
    const path = location.pathname;
    const authStore = useAuthStore.getState();

    const authPaths = ["/auth/login", "/auth/register"];
    const protectedPaths = ["/dashboard", "/settings", "/onboarding"];

    const isAuthPath = authPaths.includes(path);
    const isProtectedPath = protectedPaths.some(
      (p) => path === p || path.startsWith(`${p}/`),
    );

    let needsValidation = isAuthPath || isProtectedPath;
    let isTokenValid = false; // Assume invalid until validated

    if (needsValidation) {
      const tokenExists = !!(
        authStore.token || localStorage.getItem("auth_token")
      );

      if (tokenExists) {
        try {
          // Use refreshUser for validation. It throws on error (e.g., 401).
          // It also updates the user state on success.
          await authStore.refreshUser();
          isTokenValid = true; // If refreshUser succeeds, token is valid
        } catch (error) {
          console.warn(
            `Token validation via refreshUser failed for ${path}:`,
            error,
          );
          // Explicitly logout within beforeLoad if validation fails
          authStore.logout();
          isTokenValid = false;

          // Re-throw specific errors if needed elsewhere, but not redirect errors
          if (error instanceof Error && error.message.includes("Redirect")) {
            // Don't re-throw redirect errors from TanStack Router itself
          } else if (error instanceof UnauthorizedError) {
            // Potentially handle this specific error if needed, but logout is main action
          } else {
            // Optional: re-throw other unexpected errors
            // throw error;
          }
        }
      } else {
        console.log(
          `No token found for path: ${path}, validation skipped (result: false).`,
        );
        if (authStore.isAuthenticated) {
          authStore.logout(); // Ensure logged out if state is inconsistent
        }
        isTokenValid = false;
      }
    } else {
      // For public paths, rely on the current persisted state
      isTokenValid = authStore.isAuthenticated;
    }

    // --- Redirection Logic --- (Should now have consistent isTokenValid state)

    // Redirect authenticated users from auth paths
    if (isAuthPath && isTokenValid) {
      console.log("Redirecting authenticated user FROM auth path TO dashboard");
      throw redirect({ to: "/dashboard" });
    }

    // Redirect unauthenticated users from protected paths
    if (isProtectedPath && !isTokenValid) {
      console.log(
        "Redirecting unauthenticated user FROM protected path TO login",
      );
      throw redirect({ to: "/auth/login", search: { redirectTo: path } });
    }

    // Return potentially updated auth state for context.
    const finalAuthState = useAuthStore.getState();
    return {
      auth: {
        user: finalAuthState.user,
        isAuthenticated: finalAuthState.isAuthenticated,
      },
    };
  },
});

/**
 * Root component rendering the main layout and TanStack DevTools.
 * Authentication logic is now primarily handled in `beforeLoad`.
 */
function RootComponent() {
  // Removed the useEffect hook that called refreshUser,
  // as validation is now handled in beforeLoad.

  return (
    <>
      <HeadContent />
      <Outlet />
      <TanStackRouterDevtools />
    </>
  );
}
